import { create } from "zustand";
import { persist } from "zustand/middleware";

// Main app sidebar sizing. Width is user-resizable (drag handle) within
// [MIN, MAX]; collapsing swaps the whole rail to an icon-only RAIL width.
export const SIDEBAR_RAIL_WIDTH = 64;
export const SIDEBAR_MIN_WIDTH = 236;
export const SIDEBAR_MAX_WIDTH = 360;
export const SIDEBAR_DEFAULT_WIDTH = 248;

const clampWidth = (w: number) =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, w));

interface SidebarLayoutState {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  // Keys of expanded collapsible nav nodes — both real groups (Outreach,
  // Settings, …) and the "contacts" item's filter sub-tree, which is seeded
  // expanded below.
  expandedGroups: string[];
  setExpandedGroups: (keys: string[]) => void;
  toggleExpandedGroup: (key: string) => void;
  // Main rail: collapsed = icon-only; width = drag-resized px when expanded.
  // Both persist so the user's layout sticks across reloads.
  railCollapsed: boolean;
  setRailCollapsed: (collapsed: boolean) => void;
  toggleRailCollapsed: () => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
}

export const useSidebarLayoutStore = create<SidebarLayoutState>()(
  persist(
    (set) => ({
      isSidebarOpen: true,
      setIsSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
      toggleSidebar: () =>
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      // "contacts" starts expanded so its Segments/Lifecycles tree is visible
      // on first paint, matching the rest of the redesign's default-open
      // sub-trees.
      expandedGroups: ["contacts"],
      setExpandedGroups: (keys) => set({ expandedGroups: keys }),
      toggleExpandedGroup: (key) =>
        set((state) => ({
          expandedGroups: state.expandedGroups.includes(key)
            ? state.expandedGroups.filter((k) => k !== key)
            : [...state.expandedGroups, key],
        })),
      railCollapsed: false,
      setRailCollapsed: (collapsed) => set({ railCollapsed: collapsed }),
      toggleRailCollapsed: () =>
        set((state) => ({ railCollapsed: !state.railCollapsed })),
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      setSidebarWidth: (width) => set({ sidebarWidth: clampWidth(width) }),
    }),
    {
      name: "sidebar-layout",
      // Only the rail layout is durable; transient panel/expansion state stays
      // in memory so it resets per session.
      partialize: (state) => ({
        railCollapsed: state.railCollapsed,
        sidebarWidth: state.sidebarWidth,
      }),
    },
  ),
);
